"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { centeredMovingAverage, timeAwareMovingAverage } from "./chartMath";
import {
  findNearestChartMarker,
  type ChartHitGeometry,
  type ChartMarkerSeries,
} from "./chartHitTesting";

type QueueSample = {
  capturedAt: string;
  success: boolean;
  httpStatus: number | null;
  latencyMs: number;
  error?: string;
  shop?: {
    id: number;
    name: string;
    address: string;
    businessHours: string;
    isOpen: boolean;
    isBusy: boolean;
    isWorking: boolean;
  };
  queue?: {
    orderCount: number;
    makingCount: number;
    makingTimeSeconds: number;
    pickupAt: string;
    makingProgress: string;
    isVisible: boolean;
  };
};

type HistoryFile = {
  version: number;
  generatedAt: string | null;
  source: {
    latitude: number;
    longitude: number;
    areaCode: string;
  };
  samples: QueueSample[];
};

type ChartView = "time" | "day" | "month" | "weekday";
type WeekendFilter = "all" | "weekday" | "weekend";
type ChartSeriesKey = "making" | "wait" | "trend";
type ChartSeriesVisibility = Record<ChartSeriesKey, boolean>;

type ChartPoint = {
  key: string;
  axisPrimary: string;
  axisSecondary?: string;
  timeOfDay?: number;
  makingCount: number;
  waitMinutes: number;
  sampleCount: number;
  isWeekend: boolean;
};

const EMPTY_HISTORY: HistoryFile = {
  version: 1,
  generatedAt: null,
  source: {
    latitude: 31.22496337890625,
    longitude: 121.49887613932292,
    areaCode: "310101",
  },
  samples: [],
};

const VIEW_OPTIONS: Array<{ key: ChartView; label: string }> = [
  { key: "time", label: "时间点" },
  { key: "day", label: "按日" },
  { key: "month", label: "按月" },
  { key: "weekday", label: "按星期几" },
];

const WEEKEND_FILTER_OPTIONS: Array<{ key: WeekendFilter; label: string }> = [
  { key: "all", label: "全部" },
  { key: "weekday", label: "非周末" },
  { key: "weekend", label: "周末" },
];

const WEEKDAY_LABELS = ["周一", "周二", "周三", "周四", "周五", "周六", "周日"];

const dateTimeFormatter = new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const timeFormatter = new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const timePartsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Asia/Shanghai",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const monthDayFormatter = new Intl.DateTimeFormat("zh-CN", {
  timeZone: "Asia/Shanghai",
  month: "2-digit",
  day: "2-digit",
});

const shanghaiDayFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Shanghai",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const weekdayKeyFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "Asia/Shanghai",
  weekday: "short",
});

const WEEKDAY_INDEX = new Map([
  ["Mon", 0],
  ["Tue", 1],
  ["Wed", 2],
  ["Thu", 3],
  ["Fri", 4],
  ["Sat", 5],
  ["Sun", 6],
]);

function formatNumber(value: number | undefined, fallback = "—") {
  return Number.isFinite(value) ? String(value) : fallback;
}

function formatChartValue(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function waitMinutes(sample: QueueSample | undefined) {
  if (!sample?.queue || !Number.isFinite(sample.queue.makingTimeSeconds)) {
    return undefined;
  }
  return Math.round(sample.queue.makingTimeSeconds / 60);
}

function getShanghaiDay(value: string) {
  return shanghaiDayFormatter.format(new Date(value));
}

function getShanghaiWeekdayIndex(value: string) {
  return WEEKDAY_INDEX.get(weekdayKeyFormatter.format(new Date(value))) ?? 0;
}

function getShanghaiTimeOfDay(value: string) {
  const parts = timePartsFormatter.formatToParts(new Date(value));
  const valueFor = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  return valueFor("hour") * 60 + valueFor("minute") + valueFor("second") / 60;
}

function formatTimeOfDay(value: number) {
  const roundedMinutes = Math.max(0, Math.min(24 * 60 - 1, Math.round(value)));
  const hour = Math.floor(roundedMinutes / 60);
  const minute = roundedMinutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function formatAxisRangePoint(point: ChartPoint | undefined) {
  if (!point) return "—";
  return [point.axisPrimary, point.axisSecondary].filter(Boolean).join(" ");
}

function buildChartPoints(samples: QueueSample[], view: ChartView): ChartPoint[] {
  if (view === "time") {
    return samples
      .map((sample) => {
        const weekdayIndex = getShanghaiWeekdayIndex(sample.capturedAt);
        const capturedAt = new Date(sample.capturedAt);
        return {
          key: sample.capturedAt,
          axisPrimary: timeFormatter.format(capturedAt),
          timeOfDay: getShanghaiTimeOfDay(sample.capturedAt),
          makingCount: sample.queue?.makingCount ?? 0,
          waitMinutes: waitMinutes(sample) ?? 0,
          sampleCount: 1,
          isWeekend: weekdayIndex >= 5,
        };
      })
      .sort((left, right) => left.timeOfDay - right.timeOfDay || left.key.localeCompare(right.key));
  }

  const groups = new Map<
    string,
    {
      makingCount: number;
      waitMinutes: number;
      sampleCount: number;
      weekdayIndex: number;
    }
  >();

  samples.forEach((sample) => {
    const day = getShanghaiDay(sample.capturedAt);
    const weekdayIndex = getShanghaiWeekdayIndex(sample.capturedAt);
    const key = view === "day" ? day : view === "month" ? day.slice(0, 7) : String(weekdayIndex);
    const current = groups.get(key) ?? {
      makingCount: 0,
      waitMinutes: 0,
      sampleCount: 0,
      weekdayIndex,
    };
    current.makingCount += sample.queue?.makingCount ?? 0;
    current.waitMinutes += waitMinutes(sample) ?? 0;
    current.sampleCount += 1;
    groups.set(key, current);
  });

  return [...groups.entries()]
    .sort(([left], [right]) => {
      if (view === "weekday") return Number(left) - Number(right);
      return left.localeCompare(right);
    })
    .map(([key, group]) => {
      const divisor = Math.max(group.sampleCount, 1);
      const dateForLabel = view === "day" ? new Date(`${key}T00:00:00+08:00`) : undefined;
      return {
        key,
        axisPrimary:
          view === "day"
            ? monthDayFormatter.format(dateForLabel)
            : view === "month"
              ? key.replace("-", "/")
              : WEEKDAY_LABELS[group.weekdayIndex],
        axisSecondary: view === "day" ? WEEKDAY_LABELS[group.weekdayIndex] : undefined,
        makingCount: group.makingCount / divisor,
        waitMinutes: group.waitMinutes / divisor,
        sampleCount: group.sampleCount,
        isWeekend: group.weekdayIndex >= 5 && view !== "month",
      };
    });
}

function QueueChart({
  points,
  view,
  seriesVisibility,
}: {
  points: ChartPoint[];
  view: ChartView;
  seriesVisibility: ChartSeriesVisibility;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const geometryRef = useRef<ChartHitGeometry | null>(null);
  const maximumXIndex = Math.max(0, points.length - 1);
  const [xRange, setXRange] = useState<[number, number]>([0, 100]);
  const [yRange, setYRange] = useState<[number, number]>([0, 100]);
  const [hoverSelection, setHoverSelection] = useState<{
    index: number;
    series: ChartMarkerSeries;
    x: number;
    y: number;
    chartWidth: number;
  } | null>(null);
  const xStart = Math.round((maximumXIndex * xRange[0]) / 100);
  const xEnd = Math.max(xStart, Math.round((maximumXIndex * xRange[1]) / 100));
  const smoothedMakingValues = useMemo(() => {
    if (view === "time") {
      return timeAwareMovingAverage(
        points.map((point, index) => ({
          position: point.timeOfDay ?? index,
          value: point.makingCount,
        })),
        40,
        10,
      );
    }
    return centeredMovingAverage(points.map((point) => point.makingCount), 5);
  }, [points, view]);
  const visiblePoints = useMemo(() => points.slice(xStart, xEnd + 1), [points, xEnd, xStart]);
  const visibleTrendValues = useMemo(
    () => smoothedMakingValues.slice(xStart, xEnd + 1),
    [smoothedMakingValues, xEnd, xStart],
  );
  const hoveredIndex = hoverSelection?.index ?? null;
  const hoveredSeries = hoverSelection?.series ?? null;

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvas?.parentElement;
    if (!canvas || !container) return;

    const draw = () => {
      const width = Math.max(container.clientWidth, 240);
      const height = 360;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.floor(width * ratio);
      canvas.height = Math.floor(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const context = canvas.getContext("2d");
      if (!context) return;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);

      const plot = { left: 58, right: 58, top: 20, bottom: 78 };
      const plotWidth = width - plot.left - plot.right;
      const plotHeight = height - plot.top - plot.bottom;
      const series = [
        {
          key: "making" as const,
          color: "#ff6b2c",
          values: visiblePoints.map((point) => point.makingCount),
          axis: "left" as const,
          dash: [] as number[],
          marker: "filled" as const,
          lineWidth: 2.25,
        },
        {
          key: "wait" as const,
          color: "#48b9aa",
          values: visiblePoints.map((point) => point.waitMinutes),
          axis: "right" as const,
          dash: [7, 4],
          marker: "open" as const,
          lineWidth: 2.25,
        },
        {
          key: "trend" as const,
          color: "#62594f",
          values: visibleTrendValues,
          axis: "left" as const,
          dash: [2, 4],
          marker: "diamond" as const,
          lineWidth: 2.75,
        },
      ].filter((item) => seriesVisibility[item.key]);
      const cupMaximum = Math.max(
        5,
        ...visiblePoints.map((point) => point.makingCount),
        ...visibleTrendValues,
      );
      const waitMaximum = Math.max(5, ...visiblePoints.map((point) => point.waitMinutes));
      const cupScaleMaximum = Math.ceil(cupMaximum / 5) * 5;
      const waitScaleMaximum = Math.ceil(waitMaximum / 5) * 5;
      const cupYMinimum = (cupScaleMaximum * yRange[0]) / 100;
      const cupYMaximum = (cupScaleMaximum * yRange[1]) / 100;
      const waitYMinimum = (waitScaleMaximum * yRange[0]) / 100;
      const waitYMaximum = (waitScaleMaximum * yRange[1]) / 100;
      const cupYSpan = Math.max(0.01, cupYMaximum - cupYMinimum);
      const waitYSpan = Math.max(0.01, waitYMaximum - waitYMinimum);

      const firstTimeOfDay = visiblePoints[0]?.timeOfDay;
      const lastTimeOfDay = visiblePoints.at(-1)?.timeOfDay;
      const hasTimeOfDayScale =
        view === "time" &&
        Number.isFinite(firstTimeOfDay) &&
        Number.isFinite(lastTimeOfDay);
      const xFor = (point: ChartPoint, index: number) => {
        if (hasTimeOfDayScale && Number.isFinite(point.timeOfDay)) {
          if (firstTimeOfDay === lastTimeOfDay) return plot.left + plotWidth / 2;
          return plot.left + (plotWidth * ((point.timeOfDay ?? 0) - (firstTimeOfDay ?? 0))) / ((lastTimeOfDay ?? 0) - (firstTimeOfDay ?? 0));
        }
        if (visiblePoints.length === 1) return plot.left + plotWidth / 2;
        return plot.left + (plotWidth * index) / (visiblePoints.length - 1);
      };
      const xPositions = visiblePoints.map(xFor);
      const makingYPositions = visiblePoints.map(
        (point) => plot.top + plotHeight * (1 - (point.makingCount - cupYMinimum) / cupYSpan),
      );
      const waitYPositions = visiblePoints.map(
        (point) => plot.top + plotHeight * (1 - (point.waitMinutes - waitYMinimum) / waitYSpan),
      );
      const trendYPositions = visibleTrendValues.map(
        (value) => plot.top + plotHeight * (1 - (value - cupYMinimum) / cupYSpan),
      );
      geometryRef.current = {
        width,
        height,
        plotLeft: plot.left,
        plotRight: width - plot.right,
        plotTop: plot.top,
        plotBottom: plot.top + plotHeight,
        xPositions,
        makingYPositions,
        waitYPositions,
        trendYPositions,
        visibleSeries: (Object.keys(seriesVisibility) as ChartMarkerSeries[]).filter(
          (key) => seriesVisibility[key],
        ),
      };

      if (view === "day" || view === "weekday") {
        visiblePoints.forEach((point, index) => {
          if (!point.isWeekend) return;
          const x = xFor(point, index);
          const previousX = index > 0 ? xFor(visiblePoints[index - 1], index - 1) : plot.left;
          const nextX = index < visiblePoints.length - 1 ? xFor(visiblePoints[index + 1], index + 1) : plot.left + plotWidth;
          const bandLeft = index === 0 ? plot.left : (previousX + x) / 2;
          const bandRight = index === visiblePoints.length - 1 ? plot.left + plotWidth : (x + nextX) / 2;
          context.fillStyle = "rgba(255, 107, 44, 0.075)";
          context.fillRect(bandLeft, plot.top, Math.max(1, bandRight - bandLeft), plotHeight);
        });
      }

      context.font = "11px var(--font-geist-mono), monospace";
      context.lineWidth = 1;
      context.textBaseline = "middle";
      for (let tick = 0; tick <= 4; tick += 1) {
        const y = plot.top + (plotHeight * tick) / 4;
        const cupValue = cupYMinimum + cupYSpan * (1 - tick / 4);
        const waitValue = waitYMinimum + waitYSpan * (1 - tick / 4);
        context.strokeStyle = "rgba(25, 23, 20, 0.1)";
        context.beginPath();
        context.moveTo(plot.left, y);
        context.lineTo(width - plot.right, y);
        context.stroke();
        context.fillStyle = "rgba(25, 23, 20, 0.55)";
        context.textAlign = "right";
        context.fillText(formatChartValue(cupValue), plot.left - 9, y);
        context.textAlign = "left";
        context.fillText(formatChartValue(waitValue), width - plot.right + 9, y);
      }

      const axisBottom = plot.top + plotHeight;
      context.strokeStyle = "rgba(25, 23, 20, 0.54)";
      context.lineWidth = 1.25;
      context.beginPath();
      context.moveTo(plot.left, plot.top);
      context.lineTo(plot.left, axisBottom);
      context.lineTo(width - plot.right, axisBottom);
      context.lineTo(width - plot.right, plot.top);
      context.stroke();

      const positionedSeries = series.map((item) => ({
        ...item,
        positions: item.values.map((value, index) => ({
          x: xPositions[index],
          y:
            item.key === "trend"
              ? trendYPositions[index]
              : item.axis === "left"
                ? makingYPositions[index]
                : waitYPositions[index],
          value,
        })),
      }));

      const drawMarker = (
        item: (typeof positionedSeries)[number],
        position: { x: number; y: number },
        markerRadius: number,
        highlighted = false,
      ) => {
        const radius = markerRadius;
        context.beginPath();
        if (item.marker === "diamond") {
          context.moveTo(position.x, position.y - radius);
          context.lineTo(position.x + radius, position.y);
          context.lineTo(position.x, position.y + radius);
          context.lineTo(position.x - radius, position.y);
          context.closePath();
          context.fillStyle = "#f8f4ec";
          context.fill();
          context.strokeStyle = item.color;
          context.lineWidth = highlighted ? 2.5 : 1.5;
          context.stroke();
          return;
        }
        context.arc(position.x, position.y, radius, 0, Math.PI * 2);
        context.fillStyle = item.marker === "filled" && !highlighted ? item.color : "#f8f4ec";
        context.fill();
        context.strokeStyle = item.color;
        context.lineWidth = highlighted ? 2.5 : 1.5;
        context.stroke();
        if (highlighted) {
          context.fillStyle = item.color;
          context.beginPath();
          context.arc(position.x, position.y, 2.2, 0, Math.PI * 2);
          context.fill();
        }
      };

      context.save();
      context.beginPath();
      context.rect(plot.left, plot.top, plotWidth, plotHeight);
      context.clip();
      positionedSeries.forEach((item) => {
        context.strokeStyle = item.color;
        context.lineWidth = item.lineWidth;
        context.lineJoin = "round";
        context.lineCap = "round";
        context.setLineDash(item.dash);
        context.beginPath();
        item.positions.forEach((position, index) => {
          if (index === 0) {
            context.moveTo(position.x, position.y);
            return;
          }
          const previous = item.positions[index - 1];
          const middleX = (previous.x + position.x) / 2;
          context.bezierCurveTo(
            middleX,
            previous.y,
            middleX,
            position.y,
            position.x,
            position.y,
          );
        });
        context.stroke();
        context.setLineDash([]);

        const markerRadius = visiblePoints.length > 100 && view === "time" ? 1.3 : item.marker === "diamond" ? 2 : 2.4;
        item.positions.forEach((position) => drawMarker(item, position, markerRadius));
      });

      if (hoveredIndex !== null && visiblePoints[hoveredIndex]) {
        const hoverX = xPositions[hoveredIndex];
        context.strokeStyle = "rgba(25, 23, 20, 0.28)";
        context.lineWidth = 1;
        context.setLineDash([3, 4]);
        context.beginPath();
        context.moveTo(hoverX, plot.top);
        context.lineTo(hoverX, axisBottom);
        context.stroke();
        context.setLineDash([]);

        positionedSeries.forEach((item) => {
          const position = item.positions[hoveredIndex];
          const isNearestSeries = item.key === hoveredSeries;
          drawMarker(item, position, isNearestSeries ? 6.5 : 5, true);
        });
      }
      context.restore();

      context.textBaseline = "top";
      const drawXAxisLabel = (label: string, x: number, align: CanvasTextAlign, secondary?: string, isWeekend = false) => {
        context.strokeStyle = "rgba(25, 23, 20, 0.42)";
        context.lineWidth = 1;
        context.beginPath();
        context.moveTo(x, axisBottom);
        context.lineTo(x, axisBottom + 5);
        context.stroke();
        context.textAlign = align;
        context.fillStyle = isWeekend ? "#c44f26" : "rgba(25, 23, 20, 0.58)";
        context.fillText(label, x, axisBottom + 10);
        if (secondary) {
          context.fillStyle = isWeekend ? "#c44f26" : "rgba(25, 23, 20, 0.42)";
          context.fillText(secondary, x, axisBottom + 26);
        }
      };

      const maxLabels = Math.max(2, Math.floor(plotWidth / 88));
      if (hasTimeOfDayScale) {
        const range = Math.max(0, (lastTimeOfDay ?? 0) - (firstTimeOfDay ?? 0));
        const labelCount = range === 0 ? 1 : Math.min(maxLabels, Math.max(2, Math.floor(range) + 1));
        Array.from({ length: labelCount }, (_, index) => {
          const ratioAlongAxis = labelCount === 1 ? 0.5 : index / (labelCount - 1);
          const timeOfDay = (firstTimeOfDay ?? 0) + range * ratioAlongAxis;
          const x = plot.left + plotWidth * ratioAlongAxis;
          const align = labelCount === 1 ? "center" : index === 0 ? "left" : index === labelCount - 1 ? "right" : "center";
          drawXAxisLabel(formatTimeOfDay(timeOfDay), x, align);
        });
      } else {
        const labelCount = Math.min(visiblePoints.length, maxLabels);
        const labelIndexes = Array.from(
          new Set(
            Array.from({ length: labelCount }, (_, index) =>
              labelCount === 1 ? 0 : Math.round((index * (visiblePoints.length - 1)) / (labelCount - 1)),
            ),
          ),
        );
        labelIndexes.forEach((index) => {
          const point = visiblePoints[index];
          const x = xFor(point, index);
          const align = visiblePoints.length === 1 ? "center" : index === 0 ? "left" : index === visiblePoints.length - 1 ? "right" : "center";
          drawXAxisLabel(point.axisPrimary, x, align, point.axisSecondary, point.isWeekend);
        });
      }

      const xAxisTitle = view === "time" ? "一天内时间点" : view === "day" ? "执行日期" : view === "month" ? "执行月份" : "星期几";
      context.fillStyle = "rgba(25, 23, 20, 0.62)";
      context.textAlign = "center";
      context.textBaseline = "bottom";
      context.fillText(xAxisTitle, plot.left + plotWidth / 2, height - 4);

      context.save();
      context.translate(13, plot.top + plotHeight / 2);
      context.rotate(-Math.PI / 2);
      context.textAlign = "center";
      context.textBaseline = "top";
      context.fillText("杯数（杯）", 0, 0);
      context.restore();

      context.save();
      context.translate(width - 13, plot.top + plotHeight / 2);
      context.rotate(Math.PI / 2);
      context.textAlign = "center";
      context.textBaseline = "top";
      context.fillText("制作时间（分钟）", 0, 0);
      context.restore();
    };

    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(container);
    return () => observer.disconnect();
  }, [hoveredIndex, hoveredSeries, seriesVisibility, view, visiblePoints, visibleTrendValues, yRange]);

  const selectNearestPoint = (clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    const geometry = geometryRef.current;
    if (!canvas || !geometry || !geometry.xPositions.length) return;
    const bounds = canvas.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const localX = ((clientX - bounds.left) / bounds.width) * geometry.width;
    const localY = ((clientY - bounds.top) / bounds.height) * geometry.height;
    const nearest = findNearestChartMarker(localX, localY, geometry);
    if (!nearest) {
      setHoverSelection(null);
      return;
    }
    setHoverSelection({
      index: nearest.index,
      series: nearest.series,
      x: nearest.x,
      y: nearest.y,
      chartWidth: geometry.width,
    });
  };

  const selectPoint = (index: number) => {
    const geometry = geometryRef.current;
    if (!geometry || !visiblePoints[index]) {
      setHoverSelection(null);
      return;
    }
    const candidates: Array<{ series: ChartMarkerSeries; y: number }> = [
      { series: "making", y: geometry.makingYPositions[index] },
      { series: "wait", y: geometry.waitYPositions[index] },
      { series: "trend", y: geometry.trendYPositions?.[index] ?? Number.NaN },
    ];
    const positions = candidates.filter(
      ({ series, y }) => seriesVisibility[series] && Number.isFinite(y),
    );
    const selected = positions.sort((left, right) => left.y - right.y)[0];
    if (!selected) {
      setHoverSelection(null);
      return;
    }
    setHoverSelection({
      index,
      series: selected.series,
      x: geometry.xPositions[index],
      y: Math.max(geometry.plotTop, Math.min(geometry.plotBottom, selected.y)),
      chartWidth: geometry.width,
    });
  };

  const moveKeyboardSelection = (direction: number) => {
    const initial = direction > 0 ? -1 : visiblePoints.length;
    const nextIndex = Math.max(
      0,
      Math.min(visiblePoints.length - 1, (hoverSelection?.index ?? initial) + direction),
    );
    selectPoint(nextIndex);
  };

  const hoveredPoint = hoveredIndex === null ? undefined : visiblePoints[hoveredIndex];
  const hoveredTrendValue = hoveredIndex === null ? undefined : visibleTrendValues[hoveredIndex];
  const hoverX = hoverSelection?.x;
  const hoverY = hoverSelection?.y;
  const tooltipAlignment =
    hoverX === undefined || !hoverSelection
      ? "is-centered"
      : hoverX < 185
        ? "is-start"
        : hoverX > hoverSelection.chartWidth - 185
          ? "is-end"
          : "is-centered";
  const tooltipLabel = hoveredPoint
    ? view === "time"
      ? dateTimeFormatter.format(new Date(hoveredPoint.key))
      : [hoveredPoint.axisPrimary, hoveredPoint.axisSecondary].filter(Boolean).join(" · ")
    : "";
  const updateXStart = (nextValue: number) => {
    setXRange((current) => [Math.min(nextValue, current[1]), current[1]]);
    setHoverSelection(null);
  };
  const updateXEnd = (nextValue: number) => {
    setXRange((current) => [current[0], Math.max(nextValue, current[0])]);
    setHoverSelection(null);
  };
  const updateYStart = (nextValue: number) => {
    setYRange((current) => [Math.min(nextValue, current[1] - 5), current[1]]);
    setHoverSelection(null);
  };
  const updateYEnd = (nextValue: number) => {
    setYRange((current) => [current[0], Math.max(nextValue, current[0] + 5)]);
    setHoverSelection(null);
  };
  const resetAxisRanges = () => {
    setXRange([0, 100]);
    setYRange([0, 100]);
    setHoverSelection(null);
  };
  const xStartPercent = xRange[0];
  const xEndPercent = xRange[1];
  const axisRangesAreFull = xRange[0] === 0 && xRange[1] === 100 && yRange[0] === 0 && yRange[1] === 100;

  return (
    <div className="chart-explorer">
      <div className="axis-slider axis-slider-y">
        <div className="axis-slider-label">
          <span>纵轴范围</span>
          <output>{yRange[0]}%–{yRange[1]}%</output>
        </div>
        <div className="dual-range dual-range-y">
          <span
            className="dual-range-selection"
            style={{ bottom: `${yRange[0]}%`, height: `${yRange[1] - yRange[0]}%` }}
            aria-hidden="true"
          />
          <input
            className="range-lower"
            type="range"
            min={0}
            max={Math.max(0, yRange[1] - 5)}
            step={5}
            value={yRange[0]}
            aria-label="纵轴显示下限"
            onChange={(event) => updateYStart(Number(event.target.value))}
          />
          <input
            className="range-upper"
            type="range"
            min={Math.min(100, yRange[0] + 5)}
            max={100}
            step={5}
            value={yRange[1]}
            aria-label="纵轴显示上限"
            onChange={(event) => updateYEnd(Number(event.target.value))}
          />
        </div>
        <small>同步缩放左右轴</small>
      </div>

      <div className="queue-chart-wrap">
        <canvas
        ref={canvasRef}
        className="queue-chart"
        role="img"
        tabIndex={0}
        aria-label={`当前等待杯数、制作时间和${view === "time" ? "时间加权" : "五点"}移动平均趋势的${VIEW_OPTIONS.find((option) => option.key === view)?.label ?? "历史"}曲线图；移动鼠标选择距离指针最近的数据点，聚焦后可用左右方向键切换时间点`}
        onPointerMove={(event) => selectNearestPoint(event.clientX, event.clientY)}
        onPointerLeave={() => setHoverSelection(null)}
        onFocus={() => {
          if (!hoverSelection) selectPoint(visiblePoints.length - 1);
        }}
        onBlur={() => setHoverSelection(null)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            moveKeyboardSelection(-1);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            moveKeyboardSelection(1);
          } else if (event.key === "Home") {
            event.preventDefault();
            selectPoint(0);
          } else if (event.key === "End") {
            event.preventDefault();
            selectPoint(visiblePoints.length - 1);
          }
        }}
      />
      {hoveredPoint && hoverX !== undefined && hoverY !== undefined && (
        <div
          className={`chart-tooltip ${tooltipAlignment}`}
          style={{ left: hoverX, top: Math.max(8, hoverY - 12) }}
          role="status"
          aria-live="polite"
        >
          <p>{tooltipLabel}</p>
          {seriesVisibility.making && (
            <div className={hoveredSeries === "making" ? "is-selected" : undefined}>
              <i className="tooltip-making" aria-hidden="true" />
              <span>当前等待杯数</span>
              <strong>{formatChartValue(hoveredPoint.makingCount)} 杯</strong>
            </div>
          )}
          {seriesVisibility.wait && (
            <div className={hoveredSeries === "wait" ? "is-selected" : undefined}>
              <i className="tooltip-wait" aria-hidden="true" />
              <span>制作时间</span>
              <strong>{formatChartValue(hoveredPoint.waitMinutes)} 分钟</strong>
            </div>
          )}
          {seriesVisibility.trend && hoveredTrendValue !== undefined && (
            <div className={hoveredSeries === "trend" ? "is-selected" : undefined}>
              <i className="tooltip-trend" aria-hidden="true" />
              <span>等待杯数趋势</span>
              <strong>{formatChartValue(hoveredTrendValue)} 杯</strong>
            </div>
          )}
          {hoveredPoint.sampleCount > 1 && <small>{hoveredPoint.sampleCount} 条样本均值</small>}
        </div>
      )}
      </div>

      <button
        className="axis-reset-button"
        type="button"
        onClick={resetAxisRanges}
        disabled={axisRangesAreFull}
      >
        重置轴
      </button>
      <div className="axis-slider axis-slider-x">
        <div className="axis-slider-label">
          <span>横轴范围</span>
          <output>{formatAxisRangePoint(points[xStart])} – {formatAxisRangePoint(points[xEnd])}</output>
        </div>
        <div className="dual-range dual-range-x">
          <span
            className="dual-range-selection"
            style={{ left: `${xStartPercent}%`, width: `${Math.max(0, xEndPercent - xStartPercent)}%` }}
            aria-hidden="true"
          />
          <input
            className="range-lower"
            type="range"
            min={0}
            max={xRange[1]}
            step={1}
            value={xRange[0]}
            aria-label="横轴显示起点"
            onChange={(event) => updateXStart(Number(event.target.value))}
          />
          <input
            className="range-upper"
            type="range"
            min={xRange[0]}
            max={100}
            step={1}
            value={xRange[1]}
            aria-label="横轴显示终点"
            onChange={(event) => updateXEnd(Number(event.target.value))}
          />
        </div>
      </div>
    </div>
  );
}

function exportCsv(samples: QueueSample[]) {
  const rows = [
    ["采集时间", "状态", "HTTP状态", "前面还有几单", "当前等待杯数", "制作时间_分钟", "预计取餐", "请求耗时_毫秒", "错误"],
    ...samples.map((sample) => [
      sample.capturedAt,
      sample.success ? "成功" : "失败",
      sample.httpStatus ?? "",
      sample.queue?.orderCount ?? "",
      sample.queue?.makingCount ?? "",
      waitMinutes(sample) ?? "",
      sample.queue?.pickupAt ?? "",
      sample.latencyMs,
      sample.error ?? "",
    ]),
  ];
  const csv = rows
    .map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(","))
    .join("\r\n");
  const blob = new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `manner-queue-${new Date().toISOString().slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export default function QueueDashboard() {
  const [history, setHistory] = useState<HistoryFile>(EMPTY_HISTORY);
  const [view, setView] = useState<ChartView>("time");
  const [weekendFilter, setWeekendFilter] = useState<WeekendFilter>("all");
  const [seriesVisibility, setSeriesVisibility] = useState<ChartSeriesVisibility>({
    making: true,
    wait: true,
    trend: true,
  });
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [clockNow, setClockNow] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    const endpoints = [
      `/api/history?t=${Date.now()}`,
      `/queue-history.json?t=${Date.now()}`,
    ];
    let lastError: unknown;

    try {
      for (const endpoint of endpoints) {
        try {
          const response = await fetch(endpoint, { cache: "no-store" });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = (await response.json()) as HistoryFile;
          if (!Array.isArray(data.samples)) throw new Error("历史数据格式无效");
          setHistory({ ...EMPTY_HISTORY, ...data, samples: data.samples });
          setLoadError(null);
          return;
        } catch (error) {
          lastError = error;
        }
      }

      setLoadError(lastError instanceof Error ? lastError.message : "无法读取历史数据");
    } finally {
      setClockNow(Date.now());
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initialTimer = window.setTimeout(() => void loadHistory(), 0);
    const timer = window.setInterval(() => void loadHistory(), 30_000);
    return () => {
      window.clearTimeout(initialTimer);
      window.clearInterval(timer);
    };
  }, [loadHistory]);

  const allSamples = useMemo(
    () => [...history.samples].sort((a, b) => Date.parse(a.capturedAt) - Date.parse(b.capturedAt)),
    [history.samples],
  );
  const successfulSamples = useMemo(() => allSamples.filter((sample) => sample.success && sample.queue), [allSamples]);
  const availableStartDate = successfulSamples[0] ? getShanghaiDay(successfulSamples[0].capturedAt) : "";
  const availableEndDate = successfulSamples.at(-1) ? getShanghaiDay(successfulSamples.at(-1)!.capturedAt) : "";
  const effectiveStartDate = startDate ?? availableStartDate;
  const effectiveEndDate = endDate ?? availableEndDate;
  const dateRangeInvalid = Boolean(effectiveStartDate && effectiveEndDate && effectiveStartDate > effectiveEndDate);
  const visibleSamples = useMemo(() => {
    if (dateRangeInvalid) return [];
    return successfulSamples.filter((sample) => {
      const day = getShanghaiDay(sample.capturedAt);
      const isWeekend = getShanghaiWeekdayIndex(sample.capturedAt) >= 5;
      const matchesWeekend =
        weekendFilter === "all" ||
        (weekendFilter === "weekend" && isWeekend) ||
        (weekendFilter === "weekday" && !isWeekend);
      return (
        matchesWeekend &&
        (!effectiveStartDate || day >= effectiveStartDate) &&
        (!effectiveEndDate || day <= effectiveEndDate)
      );
    });
  }, [dateRangeInvalid, effectiveEndDate, effectiveStartDate, successfulSamples, weekendFilter]);
  const chartPoints = useMemo(() => buildChartPoints(visibleSamples, view), [view, visibleSamples]);

  const resetDateRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const toggleSeries = (key: ChartSeriesKey) => {
    setSeriesVisibility((current) => ({ ...current, [key]: !current[key] }));
  };

  const latestAttempt = allSamples.at(-1);
  const latest = successfulSamples.at(-1);
  const today = latest && clockNow ? getShanghaiDay(new Date(clockNow).toISOString()) : "";
  const todaySamples = successfulSamples.filter((sample) => getShanghaiDay(sample.capturedAt) === today);
  const todayPeak = todaySamples.length
    ? Math.max(...todaySamples.map((sample) => sample.queue?.orderCount ?? 0))
    : undefined;
  const averageWait = todaySamples.length
    ? Math.round(todaySamples.reduce((sum, sample) => sum + (waitMinutes(sample) ?? 0), 0) / todaySamples.length)
    : undefined;
  const recentRows = [...visibleSamples].reverse().slice(0, 200);
  const latestAgeMinutes = latestAttempt
    ? Math.max(0, Math.floor(((clockNow ?? Date.parse(latestAttempt.capturedAt)) - Date.parse(latestAttempt.capturedAt)) / 60_000))
    : undefined;
  const chartSubtitle =
    view === "time"
      ? "横轴按一天内时刻排列；多日数据按时间点混合显示"
      : view === "day"
        ? "按执行日期计算平均值，周六日以浅橙底色标识"
        : view === "month"
          ? "按执行月份计算筛选范围内的平均值"
          : "按星期几汇总平均值，便于比较一周节奏";
  const dateRangeLabel =
    effectiveStartDate && effectiveEndDate
      ? `${effectiveStartDate} 至 ${effectiveEndDate}`
      : effectiveStartDate
        ? `${effectiveStartDate} 起`
        : effectiveEndDate
          ? `截至 ${effectiveEndDate}`
          : "全部日期";
  const weekendFilterLabel =
    weekendFilter === "weekend" ? "仅周六日" : weekendFilter === "weekday" ? "仅周一至周五" : "全部星期";

  return (
    <main>
      <section className="hero-shell">
        <header className="topbar">
          <div className="brand-lockup">
            <span className="brand-mark">MQ</span>
            <div>
              <p className="eyebrow">MANNER QUEUE OBSERVER</p>
              <p className="brand-subtitle">门店排队观察</p>
            </div>
          </div>
          <div className={`collector-state ${latestAttempt?.success ? "is-live" : "is-down"}`}>
            <span className="status-dot" aria-hidden="true" />
            {latestAttempt
              ? latestAttempt.success
                ? `${latestAgeMinutes === 0 ? "刚刚" : `${latestAgeMinutes} 分钟前`}已采集`
                : "最近一次采集失败"
              : "等待首次采集"}
          </div>
        </header>

        <div className="hero-copy">
          <div>
            <p className="section-kicker">LIVE STORE SIGNAL</p>
            <h1>{latest?.shop?.name ?? "外滩 BFC 门店"}</h1>
            <p className="store-address">
              {latest?.shop?.address ?? "启动采集器后，这里会显示接口返回的门店与排队数据。"}
            </p>
          </div>
          <div className="store-facts">
            <div>
              <span>营业状态</span>
              <strong>{latest ? (latest.shop?.isOpen ? "营业中" : "已打烊") : "—"}</strong>
            </div>
            <div>
              <span>营业时间</span>
              <strong>{latest?.shop?.businessHours ?? "—"}</strong>
            </div>
          </div>
        </div>

        <div className="live-grid">
          <article className="live-card live-card-primary">
            <span className="metric-label">前面还有几单</span>
            <div className="metric-value">
              {formatNumber(latest?.queue?.orderCount)} <small>单</small>
            </div>
            <span className="metric-note">当前排在你前面的订单</span>
          </article>
          <article className="live-card">
            <span className="metric-label">当前等待杯数</span>
            <div className="metric-value">
              {formatNumber(latest?.queue?.makingCount)} <small>杯</small>
            </div>
            <span className="metric-note">当前等待制作的饮品杯数</span>
          </article>
          <article className="live-card">
            <span className="metric-label">制作时间</span>
            <div className="metric-value">
              {formatNumber(waitMinutes(latest))} <small>分钟</small>
            </div>
            <span className="metric-note">由接口秒数换算</span>
          </article>
          <article className="live-card pickup-card">
            <span className="metric-label">预计取餐</span>
            <div className="pickup-value">{latest?.queue?.pickupAt ?? "—"}</div>
            <span className="metric-note">以小程序实时提示为准</span>
          </article>
        </div>
      </section>

      <section className="content-shell">
        {loadError && (
          <div className="notice" role="status">
            历史数据暂时无法读取：{loadError}。页面会自动重试。
          </div>
        )}

        <div className="summary-strip">
          <div>
            <span>今日前面最多有几单</span>
            <strong>{formatNumber(todayPeak)} 单</strong>
          </div>
          <div>
            <span>今日平均制作时间</span>
            <strong>{formatNumber(averageWait)} 分钟</strong>
          </div>
          <div>
            <span>今日有效样本</span>
            <strong>{todaySamples.length} 个</strong>
          </div>
          <div>
            <span>最近请求耗时</span>
            <strong>{latestAttempt ? `${latestAttempt.latencyMs} ms` : "—"}</strong>
          </div>
        </div>

        <article className="panel chart-panel">
          <div className="panel-heading">
            <div>
              <p className="section-kicker">QUEUE TIMELINE</p>
              <h2>排队趋势</h2>
              <p className="chart-subtitle">{chartSubtitle}</p>
            </div>
            <div className="panel-actions">
              <button className="ghost-button" type="button" onClick={() => exportCsv(visibleSamples)} disabled={!visibleSamples.length}>
                导出 CSV
              </button>
            </div>
          </div>

          <div className="chart-controls">
            <div className="view-switch" role="group" aria-label="选择趋势查看方式">
              {VIEW_OPTIONS.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  className={view === option.key ? "is-active" : ""}
                  aria-pressed={view === option.key}
                  onClick={() => setView(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="weekend-filter">
              <span>是否周末</span>
              <div className="weekend-switch" role="group" aria-label="按是否为周六周日筛选数据">
                {WEEKEND_FILTER_OPTIONS.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    className={weekendFilter === option.key ? "is-active" : ""}
                    aria-pressed={weekendFilter === option.key}
                    onClick={() => setWeekendFilter(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="date-filter" role="group" aria-label="按起始日期和结束日期筛选">
              <label className="date-field">
                <span>起始日期</span>
                <input
                  type="date"
                  value={effectiveStartDate}
                  min={availableStartDate || undefined}
                  max={effectiveEndDate || availableEndDate || undefined}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </label>
              <span className="date-separator" aria-hidden="true">至</span>
              <label className="date-field">
                <span>结束日期</span>
                <input
                  type="date"
                  value={effectiveEndDate}
                  min={effectiveStartDate || availableStartDate || undefined}
                  max={availableEndDate || undefined}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </label>
              <button className="reset-date-button" type="button" onClick={resetDateRange} disabled={!availableStartDate}>
                全部日期
              </button>
            </div>
          </div>

          {dateRangeInvalid && <p className="date-range-error" role="alert">起始日期不能晚于结束日期。</p>}

          <div className="legend" role="group" aria-label="开启或关闭图表曲线">
            <button
              type="button"
              className={seriesVisibility.making ? "" : "is-hidden"}
              aria-pressed={seriesVisibility.making}
              onClick={() => toggleSeries("making")}
            >
              <i className="legend-cups" aria-hidden="true" />当前等待杯数（杯）
            </button>
            <button
              type="button"
              className={seriesVisibility.wait ? "" : "is-hidden"}
              aria-pressed={seriesVisibility.wait}
              onClick={() => toggleSeries("wait")}
            >
              <i className="legend-wait" aria-hidden="true" />制作时间（分钟）
            </button>
            <button
              type="button"
              className={seriesVisibility.trend ? "" : "is-hidden"}
              aria-pressed={seriesVisibility.trend}
              onClick={() => toggleSeries("trend")}
            >
              <i className="legend-trend" aria-hidden="true" />等待杯数趋势（{view === "time" ? "时间加权移动平均" : "5 点移动平均"}）
            </button>
            {(view === "day" || view === "weekday") && <span><i className="legend-weekend" />周六日</span>}
          </div>

          <div className="chart-meta">
            <span>{dateRangeLabel} · {weekendFilterLabel}</span>
            <span>{view === "time" ? `${chartPoints.length} 个时间点` : `${chartPoints.length} 个汇总点 · ${visibleSamples.length} 条样本`}</span>
          </div>

          <div className="chart-frame">
            {chartPoints.length ? (
              <QueueChart
                key={`${view}:${weekendFilter}:${effectiveStartDate}:${effectiveEndDate}`}
                points={chartPoints}
                view={view}
                seriesVisibility={seriesVisibility}
              />
            ) : (
              <div className="empty-state">
                <strong>{loading ? "正在读取历史…" : dateRangeInvalid ? "日期范围无效" : "所选条件内没有有效数据"}</strong>
                <span>{dateRangeInvalid ? "请调整起始日期或结束日期。" : "可切换周末条件、扩大日期范围，或等待采集器写入新记录。"}</span>
              </div>
            )}
          </div>
        </article>

        <article className="panel table-panel">
          <div className="panel-heading">
            <div>
              <p className="section-kicker">SAMPLE LOG</p>
              <h2>时间点明细</h2>
            </div>
            <p className="table-caption">
              {visibleSamples.length > 200
                ? `显示最近 200 / ${visibleSamples.length} 条，可导出全部`
                : `${visibleSamples.length} 条有效记录`}
            </p>
          </div>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>采集时间</th>
                  <th>前面还有几单</th>
                  <th>当前等待杯数</th>
                  <th>制作时间</th>
                  <th>预计取餐</th>
                  <th>请求耗时</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.map((sample) => (
                  <tr key={sample.capturedAt}>
                    <td>{dateTimeFormatter.format(new Date(sample.capturedAt))}</td>
                    <td className="number-cell">{sample.queue?.orderCount ?? "—"}</td>
                    <td className="number-cell">{sample.queue?.makingCount ?? "—"} 杯</td>
                    <td className="number-cell">{formatNumber(waitMinutes(sample))} 分钟</td>
                    <td>{sample.queue?.pickupAt ?? "—"}</td>
                    <td className="muted-cell">{sample.latencyMs} ms</td>
                  </tr>
                ))}
                {!recentRows.length && (
                  <tr>
                    <td colSpan={6} className="empty-row">等待采集记录</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <footer>
          <span>云端采集器按门店营业时间每 10 分钟记录一次</span>
          <span>页面每 30 秒自动读取 D1 最新历史</span>
        </footer>
      </section>
    </main>
  );
}
